import clsx from "clsx";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { ExpandableText } from "./ExpandableText";

export interface DetailTableRow {
    label: string | React.ReactNode;
    value: string | number | React.ReactNode;
    onClick?: () => void;
    valueClassName?: string;
    source?: string | null;
    sourceClassName?: string;
}

interface DetailTableProps {
    title: string;
    data: DetailTableRow[];
    className?: string;
    icon?: React.ReactNode;
    href?: string;
    isLoading?: boolean;
}

export function DetailTable({
    title,
    data,
    className,
    icon,
    href,
    isLoading = false,
}: DetailTableProps) {
    return (
        <div className={clsx("w-full", className)}>
            <h4 className="heading-m mb-3">{title}</h4>
            <div
                className={clsx(
                    "grid grid-cols-1",
                    "gap-0 overflow-hidden",
                    "sm:grid-cols-2 sm:gap-x-4",
                )}
            >
                {data.map((row, index) => {
                    const rowInGrid = Math.floor(index / 2);
                    const isEvenRow = rowInGrid % 2 === 0;

                    return (
                        <div
                            key={index}
                            className={clsx(
                                "flex items-center justify-between gap-4 border-x border-slate-700 px-4 py-3",
                                isEvenRow ? "bg-[#2E3849]" : "bg-[#353E4F]",
                                row.onClick &&
                                    "cursor-pointer hover:opacity-90",
                                index === 0 &&
                                    "rounded-t border-x border-t border-slate-700",
                                index === 1 &&
                                    "border-x border-t border-slate-700 sm:rounded-t",
                                index === data.length - 1 &&
                                    "rounded-b border-x border-b border-slate-700",
                                index === data.length - 2 &&
                                    "border-x border-b border-slate-700 sm:rounded-b",
                            )}
                            onClick={row.onClick}
                        >
                            <span className="heading-s w-2/5">{row.label}</span>
                            {href && (
                                <div
                                    className={clsx(
                                        "group flex w-3/5 items-center gap-2",
                                    )}
                                >
                                    <a
                                        href={href}
                                        className="text-link"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {row.value}
                                    </a>
                                    {icon && icon}
                                </div>
                            )}
                            {!href && (
                                <div
                                    className={clsx(
                                        "flex w-3/5 flex-col items-end",
                                    )}
                                >
                                    {isLoading ? (
                                        <div
                                            className={clsx(
                                                "h-3 w-16 bg-slate-700",
                                                "animate-pulse rounded",
                                            )}
                                        />
                                    ) : (
                                        <>
                                            {typeof row.value === "string" ? (
                                                <div className="body-s text-right">
                                                    <ExpandableText
                                                        text={row.value}
                                                        maxLength={200}
                                                        className={clsx(
                                                            row.value ===
                                                                COMING_SOON &&
                                                                "text-disabled",
                                                            row.valueClassName,
                                                        )}
                                                    />
                                                </div>
                                            ) : (
                                                <span
                                                    className={clsx(
                                                        "body-s text-right",
                                                        row.valueClassName,
                                                    )}
                                                >
                                                    {row.value}
                                                </span>
                                            )}
                                            {row.source && (
                                                <span
                                                    className={clsx(
                                                        "break-all text-right text-[9px] tracking-wide",
                                                        row.sourceClassName,
                                                    )}
                                                >
                                                    {row.source}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
